import React from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheckIcon, MapPinIcon, RepeatIcon } from 'lucide-react';
import { Rating } from '../ui/Rating';
import { Badge } from '../ui/Badge';
import { ButtonLink } from '../ui/Button';
import { SaveButton } from './ChefCard';
import { cuisineLabel } from '../../data/taxonomy';
import { fromPricePerGuest, fromPriceUnit, signatureMenu, menuQualifier } from '../../utils/pricing';
import { currency, responseTime } from '../../utils/format';
import { useLocale } from '../../contexts/LocaleContext';
import type { Chef } from '../../types/chef';

/** The top match — deliberately heavier than the rest of the grid. */
export function FeaturedChefCard({
  chef,
  reason
}: {
  chef: Chef;
  reason: string;
}) {
  const {
    locale,
    currencyCode,
    t
  } = useLocale();
  const menu = signatureMenu(chef);
  const price = fromPricePerGuest(chef);
  const unit = fromPriceUnit(chef);
  return <article className="relative overflow-hidden rounded-2xl border border-hairline bg-surface" data-id="element-308" data-mp="2kzvnp80q46va">
      <div className="grid gap-0 sm:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]" data-id="element-309" data-mp="wm9cle1d5a7l">
        <div className="relative" data-id="element-310" data-mp="11j9b93vvvaoo">
          <img src={chef.photo} alt={chef.name} className="h-56 w-full object-cover sm:h-full" loading="lazy" data-id="element-311" data-mp="11kav9xuvn7ij" />
          <SaveButton chefId={chef.id} className="absolute right-3 top-3" data-id="element-312" data-mp="16l89y0gohjfa" />
          <Badge tone="primary" className="absolute left-3 top-3" data-id="element-313" data-mp="1bjtci6jekf6w">
            Top match
          </Badge>
        </div>

        <div className="flex flex-col p-5 lg:p-6" data-id="element-314" data-mp="1fhr3newqhbcx">
          <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-action" data-id="element-315" data-mp="1khva0c3xgppf">{reason}</p>
          <div className="mt-2 flex flex-wrap items-start justify-between gap-3" data-id="element-316" data-mp="1paogb4f1o63m">
            <h3 className="font-display text-2xl font-semibold tracking-[-0.02em] text-ink" data-id="element-317" data-mp="1u99j9blol3vr">
              <Link to={`/chefs/${chef.slug}`} className="hover:text-primary" data-id="element-318" data-mp="1z8cmcqi3p013">
                {chef.name}
              </Link>
            </h3>
            <Rating value={chef.rating} count={chef.reviewCount} data-id="element-319" data-mp="239xy28iwf89p" />
          </div>

          <p className="mt-2 max-w-[52ch] text-sm leading-relaxed text-ink-2" data-id="element-320" data-mp="45fwvgykdshk">{chef.headline}</p>

          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3" data-id="element-321" data-mp="3vc8wwhhmflki">
            <div data-id="element-322" data-mp="cx83fgkgwhgv">
              <dt className="text-[11px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-323" data-mp="cxro351i5jyl">Signature</dt>
              <dd className="mt-0.5 text-sm font-medium text-ink" data-id="element-324" data-mp="84orsp5cbwep">{menu.name}</dd>
            </div>
            <div data-id="element-325" data-mp="mtkus7sjij8g">
              <dt className="text-[11px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-326" data-mp="mx8rffta7lre">From</dt>
              <dd className="tnum mt-0.5 text-sm font-medium text-ink" data-id="element-327" data-mp="hy5njyp1fll0">
                {currency(price, locale, currencyCode)} / {unit === 'guest' ? t('common.perGuest') : t('common.perPortion')}
              </dd>
            </div>
            <div data-id="element-328" data-mp="2xmnp5x4txfzn">
              <dt className="text-[11px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-329" data-mp="2xo8lnsapf905">Replies in</dt>
              <dd className="mt-0.5 text-sm font-medium text-ink" data-id="element-330" data-mp="2tmx326q8zo1g">{responseTime(chef.responseMinutes)}</dd>
            </div>
          </dl>

          <p className="mt-3 text-[12px] text-ink-3" data-id="element-331" data-mp="2jk18ecd70diu">{menuQualifier(menu)}</p>

          <ul className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] text-ink-2" data-id="element-332" data-mp="2nih2f07oym5r">
            <li className="flex items-center gap-1.5" data-id="element-333" data-mp="29rlrlidmtuzj">
              <MapPinIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-334" data-mp="2eq5dxjw518sf" /> {chef.neighbourhood}
            </li>
            <li className="flex items-center gap-1.5" data-id="element-335" data-mp="1zsmr4n30t5nm">
              <RepeatIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-336" data-mp="1zw0thvbopev2" /> {chef.repeatRate}% book again
            </li>
            <li data-id="element-337" data-mp="1qt9gvs72od1u">{chef.travelIncludedKm} km travel included</li>
            <li className="flex items-center gap-1.5 text-primary" data-id="element-338" data-mp="1quuddncy662c">
              <BadgeCheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-339" data-mp="1vt49c06hxap0" />
              {chef.verifications.filter(v => v.status === 'verified').length} checks passed
            </li>
          </ul>

          <div className="mt-5 flex flex-wrap items-center gap-2" data-id="element-340" data-mp="3q4spg5ogrjaz">
            <ButtonLink to={`/request/${chef.slug}`} variant="action" size="md" data-id="element-341" data-mp="1q4e0rxgppawn">
              {t('common.request')}
            </ButtonLink>
            <ButtonLink to={`/chefs/${chef.slug}`} variant="outline" size="md" data-id="element-342" data-mp="1l5avcbwtl2o5">
              {t('common.viewProfile')}
            </ButtonLink>
            <span className="ml-auto flex flex-wrap gap-1.5" data-id="element-343" data-mp="1c4360jca7c67">
              {chef.cuisineIds.slice(0, 2).map(id => <span key={id} className="rounded-full bg-oat px-2.5 py-1 text-[12px] font-medium text-ink-2" data-id="element-344" data-mp="1759sws0wpn6k">
                  {cuisineLabel(id)}
                </span>)}
            </span>
          </div>
        </div>
      </div>
    </article>;
}